/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";

import { BEM, TTranslateHookFn, useTranslate } from "@adapptio/lib-components";
import { v4 as uuid_v4 } from "uuid";

import { Panel } from "../../../../ui/components/Panel";
import {
	ITreeNode,
	TREE_VIEW_STYLE,
	TTreeViewNodeDragStartHandler
} from "../../../../ui/components/Tree/TreeViewTypes";
import { TreeView } from "../../../../ui/components/Tree/TreeView";
import { useTreeState } from "../../../../ui/components/Tree/useTreeState";
import { ActionEditorView } from "../ActionEditorView";
import { useBlueprintModel } from "../../../../ui/hooks/useBlueprintModel";
import { FLOW_EDITOR_DND_MIMETYPE } from "../../../../ui/components/FlowEditor/FlowEditor";
import { ISchemaFlowNodeListModel, TGetBlueprintSchemaDefault } from "@adapptio/lib-blueprint";
import { RESOURCE_TYPES, TBlueprintActionNodeTypes } from "@adapptio/lib-core";
import { IIntegrationType } from "../../../../integration/IntegrationServiceTypes";
import { IEditorResource_Integration } from "../../../../resources/ResourceTypes";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE } from "../../../../ui/components/ActionButton";
import { ActionCommandButton } from "../../../../ui/components/ActionCommandButton";

enum PALETTE_ITEM_TYPE {
	BASE_NODE = "baseNode",
	INTEGRATION_FUNCTION = "integrationFunction"
}

export interface IPaletteTreeNode extends ITreeNode {
	metaData?: {
		paletteItemType?: PALETTE_ITEM_TYPE;
		nodeType?: string;
		integrationType?: string;
		functionName?: string;
		integrationId?: string;
	};
}

function getTreeNodes(
	t: TTranslateHookFn,
	flowListModel: ISchemaFlowNodeListModel<TBlueprintActionNodeTypes>,
	integrationTypes: IIntegrationType[],
	integrationInstances: IEditorResource_Integration[]
): IPaletteTreeNode[] {
	const nodeTypes = flowListModel.schema.opts.nodeTypes;
	const integrationTypesIndex: { [K: string]: IIntegrationType } = {};

	const baseNodes: IPaletteTreeNode[] = [];
	const integrationNodes: IPaletteTreeNode[] = [];

	const rootClass = BEM("edt-module-action-editor-node-palette__tree-node-section");
	const functionClass = BEM("edt-module-action-editor-node-palette__tree-node-function");

	// Build integration type index
	integrationTypes.forEach((type) => {
		integrationTypesIndex[type.integrationType] = type;
	});

	// Add base node types
	for (const k in nodeTypes) {
		if (!nodeTypes[k].editorOptions?.displayInPalette) {
			continue;
		}

		baseNodes.push({
			id: "node_" + k,
			label: t("editor", nodeTypes[k].label),
			icon: nodeTypes[k].icon,
			children: null,
			isLoadable: false,
			selectable: true,
			metaData: {
				paletteItemType: PALETTE_ITEM_TYPE.BASE_NODE,
				nodeType: k
			},
			className: functionClass.toClassName()
		});
	}

	// Add integration nodes
	integrationInstances.forEach((resource) => {
		const type = integrationTypesIndex[resource.integrationType];

		// Skip if we cannot resolve type to prevent crash
		if (!type) {
			return;
		}

		const functionNodes: IPaletteTreeNode[] = [];

		type.functions.forEach((fn) => {
			functionNodes.push({
				id: "int_" + resource.id + "_fn_" + fn.functionName,
				label: t("editor", fn.label),
				icon: fn.icon,
				children: null,
				isLoadable: false,
				selectable: true,
				metaData: {
					paletteItemType: PALETTE_ITEM_TYPE.INTEGRATION_FUNCTION,
					integrationType: type.integrationType,
					functionName: fn.functionName,
					integrationId: resource.id
				},
				className: functionClass.toClassName()
			});
		});

		integrationNodes.push({
			id: "int_" + resource.id,
			label: resource.descriptor.name,
			description: t("editor", type.label),
			icon: type.icon,
			children: functionNodes,
			isLoadable: false,
			selectable: true,
			metaData: {}
		});
	});

	// Add root nodes
	return [
		{
			id: "integrationNodes",
			label: "Integrations",
			defaultExpanded: true,
			children: integrationNodes,
			className: rootClass.toClassName()
		},
		{
			id: "baseNodes",
			label: "Common Functions",
			defaultExpanded: true,
			children: baseNodes,
			className: rootClass.toClassName()
		}
	];
}

export interface IActionEditorNodePaletteProps {
	view: ActionEditorView;
}

export const ActionEditorNodePalette: React.FunctionComponent<IActionEditorNodePaletteProps> = ({ view }) => {
	const t = useTranslate();

	const resourceSvc = view.getResourceService();
	const { model: flowListModel } = useBlueprintModel(
		view.getActionModel().getModel().props.spec.props.nodes.value,
		"flowNodeList"
	);

	const [ integrationInstances, setIntegrationInstances ] = useState(() => {
		return resourceSvc.getResourceListByType(RESOURCE_TYPES.INTEGRATION) as IEditorResource_Integration[];
	});

	const [ treeState, handleTreeStateUpdate ] = useTreeState({
		nodes: {
			baseNodes: { expanded: true },
			integrationNodes: { expanded: true }
		}
	});

	const treeNodes = useMemo(() => {
		if (flowListModel) {
			return getTreeNodes(t, flowListModel, view.getIntegrationTypesList(), integrationInstances);
		}
	}, [ t, view, flowListModel, integrationInstances ]);

	const isDraggable = useCallback((node: ITreeNode) => {
		return !!node.metaData?.paletteItemType;
	}, []);

	const allowDrop = useCallback(() => false, []);

	const handleDragStart = useCallback<TTreeViewNodeDragStartHandler>((evData) => {
		const node = evData.draggedNode as IPaletteTreeNode;

		switch (node.metaData?.paletteItemType) {
			case PALETTE_ITEM_TYPE.BASE_NODE: {
				evData.event.dataTransfer.setData(
					FLOW_EDITOR_DND_MIMETYPE,
					JSON.stringify({
						id: uuid_v4(),
						varName: null,
						type: node.metaData.nodeType,
						opts: {},
						outputs: {},
						position: {
							x: 0,
							y: 0
						}
					})
				);
				evData.event.dataTransfer.effectAllowed = "move";

				break;
			}
			case PALETTE_ITEM_TYPE.INTEGRATION_FUNCTION: {
				evData.event.dataTransfer.setData(
					FLOW_EDITOR_DND_MIMETYPE,
					JSON.stringify({
						id: uuid_v4(),
						varName: null,
						type: "integration",
						opts: {
							integrationType: node.metaData.integrationType,
							functionName: node.metaData.functionName,
							integrationId: node.metaData.integrationId,
							params: undefined
						} as TGetBlueprintSchemaDefault<TBlueprintActionNodeTypes["integration"]["opts"]>,
						outputs: {},
						position: {
							x: 0,
							y: 0
						}
					})
				);
				evData.event.dataTransfer.effectAllowed = "move";

				break;
			}
		}
	}, []);

	// Keep integration instances up to date
	useSimpleEvent(
		resourceSvc.onResourceTypeChange,
		(ev) => {
			if (ev === RESOURCE_TYPES.INTEGRATION) {
				setIntegrationInstances(
					resourceSvc
						.getResourceListByType(RESOURCE_TYPES.INTEGRATION)
						.slice() as IEditorResource_Integration[]
				);
			}
		},
		[ resourceSvc, true ]
	);

	const baseClass = BEM("edt-module-action-editor-node-palette");

	return (
		<Panel scroll={true} className={baseClass.toClassName()}>
			<TreeView
				items={treeNodes}
				state={treeState}
				onStateUpdate={handleTreeStateUpdate}
				onNodeDragStart={handleDragStart}
				isDraggable={isDraggable}
				allowDrop={allowDrop}
				style={TREE_VIEW_STYLE.DEFAULT}
				compact={false}
			/>
			{integrationInstances.length === 0 ? (
				<div className={baseClass.element("integration-add").toClassName()}>
					<ActionCommandButton
						label="Create Integration"
						icon="mdi/plus"
						size={ACTION_BUTTON_SIZE.SMALL}
						style={ACTION_BUTTON_STYLE.PRIMARY}
						commandId="integrationEditor.createPage.open"
					/>
				</div>
			) : null}
		</Panel>
	);
};
